// MCHD Room Scheduler - Room Detail Hook
// Created: 07-10-2025 14:01:32
// Purpose: React Query hook for room detail data

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { getRoomDetail } from '../../api/endpoints/rooms';

/**
 * Room Detail Params Interface
 */
export interface RoomDetailParams {
  startDate?: string;
  endDate?: string;
}

/**
 * Use Room Detail
 * 
 * React Query hook to fetch comprehensive room detail data including:
 * - Basic room information
 * - Schedule (assignments)
 * - Blocks (maintenance/cleaning)
 * - Conflicts (failed assignments)
 * - Utilization statistics (last 4 weeks)
 * 
 * @param roomId - Room ID (UUID)
 * @param params - Optional query parameters for date filtering
 * @returns UseQueryResult with room detail data
 * 
 * @example
 * ```tsx
 * const { data, isLoading, error } = useRoomDetail('room-123', {
 *   startDate: '2025-10-01',
 *   endDate: '2025-10-31'
 * });
 * ```
 * 
 * Created: 07-10-2025 14:01:32
 * Complies: @react-frontend.mdc, @tech-stack-documentation.mdc
 */
export function useRoomDetail(
  roomId: string | undefined,
  params?: RoomDetailParams
): UseQueryResult<any, Error> {
  return useQuery({
    queryKey: ['room-detail', roomId, params],
    queryFn: async () => {
      if (!roomId) {
        throw new Error('Room ID is required');
      }
      
      const response = await getRoomDetail(roomId, params);
      
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch room detail');
      }
      
      return response.data;
    },
    enabled: !!roomId, // Only run query if roomId exists
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export default useRoomDetail;
